import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { normalizePageSlug, isReservedPageSlug } from "@profolio/validators";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Globe, AlertCircle, ChevronDown, ChevronUp, Lock, History } from "lucide-react";
import { toast } from "sonner";
import type { PageItem } from "./types";

interface PageSettingsModalProps {
  page: PageItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingSlugs: string[];
  subdomain?: string;
  onSuccess?: () => void;
}

export function PageSettingsModal({
  page,
  open,
  onOpenChange,
  existingSlugs,
  subdomain,
  onSuccess,
}: PageSettingsModalProps) {
  const [title, setTitle] = useState("");
  const [navigationLabel, setNavigationLabel] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [visible, setVisible] = useState(true);
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");

  const [slugError, setSlugError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showSeo, setShowSeo] = useState(false);

  const utils = trpc.useUtils();

  useEffect(() => {
    if (page && open) {
      setTitle(page.title || "");
      setNavigationLabel(page.navigationLabel || page.title || "");
      setSlug(page.slug || "");
      setDescription(page.description || "");
      setVisible(page.visible ?? true);
      setSeoTitle(page.seoTitle || "");
      setSeoDescription(page.seoDescription || "");
      setSlugError(null);
      setServerError(null);
    }
  }, [page, open]);

  const updatePageMutation = trpc.pages.updatePage.useMutation({
    onSuccess: (updated: { title: string }) => {
      toast.success(`Page "${updated.title}" settings saved!`);
      utils.pages.listPages.invalidate();
      utils.profile.getMyProfile.invalidate();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err: { message?: string }) => {
      setServerError(err.message || "Failed to update page settings.");
    },
  });

  const validateSlug = (val: string): string | null => {
    if (page?.isHome) return null; // Home page slug is fixed to ""
    const normalized = normalizePageSlug(val);
    if (!normalized || normalized.trim() === "") {
      return "URL slug cannot be empty.";
    }
    if (isReservedPageSlug(normalized)) {
      return `The slug '${normalized}' is reserved by the platform.`;
    }
    // Filter out this page's own original slug from collision check
    const otherSlugs = existingSlugs.filter(
      (s) => s.toLowerCase() !== (page?.slug || "").toLowerCase()
    );
    if (otherSlugs.some((s) => s.toLowerCase() === normalized.toLowerCase())) {
      return `A page with the slug '/${normalized}' already exists.`;
    }
    return null;
  };

  const handleSlugChange = (val: string) => {
    if (page?.isHome) return;
    const normalized = normalizePageSlug(val);
    setSlug(normalized);
    setSlugError(validateSlug(normalized));
    setServerError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!page) return;
    setServerError(null);

    if (!page.isHome) {
      const error = validateSlug(slug);
      if (error) {
        setSlugError(error);
        return;
      }
    }

    if (!title.trim()) {
      setServerError("Page title is required.");
      return;
    }

    updatePageMutation.mutate({
      pageId: page.id,
      title: title.trim(),
      navigationLabel: navigationLabel.trim() || title.trim(),
      slug: page.isHome ? "" : slug,
      description: description.trim() || null,
      visible: page.isHome ? true : visible,
      seoTitle: seoTitle.trim() || null,
      seoDescription: seoDescription.trim() || null,
    });
  };

  if (!page) return null;

  const isSlugChanged = !page.isHome && slug !== page.slug;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={page.isHome ? "Home Page Settings" : "Page Settings"}
      description="Configure routing, navigation labels, and SEO metadata."
    >
      <form onSubmit={handleSubmit} className="space-y-4 pt-2">
        {serverError && (
          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-xs text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{serverError}</span>
          </div>
        )}

        <div className="space-y-3.5">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-page-title" className="text-xs font-semibold">
              Page Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit-page-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="h-9 text-sm"
            />
          </div>

          {/* Navigation Label */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-page-nav-label" className="text-xs font-semibold">
              Navigation Label
            </Label>
            <Input
              id="edit-page-nav-label"
              value={navigationLabel}
              onChange={(e) => setNavigationLabel(e.target.value)}
              placeholder={title}
              className="h-9 text-sm"
            />
            <p className="text-[11px] text-muted-foreground">
              Text shown in navigation headers and drawer menus.
            </p>
          </div>

          {/* URL Slug */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-page-slug" className="text-xs font-semibold">
                URL Slug
              </Label>
              {page.isHome && (
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground font-medium">
                  <Lock className="h-3 w-3" /> Root Path Locked
                </span>
              )}
            </div>
            <div className={`flex items-center rounded-md border border-input ${
              page.isHome ? "bg-muted/70 opacity-80 cursor-not-allowed" : "bg-muted/40 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent"
            } px-3 h-9 text-sm`}>
              <Globe className="h-3.5 w-3.5 text-muted-foreground mr-1 shrink-0" />
              <span className="text-muted-foreground text-xs select-none">
                {subdomain ? `${subdomain}.profolio.lk/` : "/"}
              </span>
              <input
                id="edit-page-slug"
                type="text"
                value={page.isHome ? "" : slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                disabled={page.isHome}
                className="bg-transparent border-none outline-none text-foreground flex-1 text-sm font-mono lowercase disabled:cursor-not-allowed"
              />
            </div>

            {slugError ? (
              <p className="text-[11px] text-destructive font-medium">{slugError}</p>
            ) : isSlugChanged ? (
              <div className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                <History className="h-3 w-3 shrink-0" />
                <span>
                  A permanent 301 redirect will be created from <code>/{page.slug}</code> to <code>/{slug}</code>.
                </span>
              </div>
            ) : page.isHome ? (
              <p className="text-[11px] text-muted-foreground">
                The Home page always serves the root profile address.
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Custom path slug for this page.
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-page-desc" className="text-xs font-semibold">
              Page Description
            </Label>
            <Textarea
              id="edit-page-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description or overview..."
              rows={2}
              className="text-xs resize-none"
            />
          </div>

          {/* Visibility Toggle */}
          <div className={`flex items-center justify-between p-3 rounded-lg border border-border ${
            page.isHome ? "bg-muted/50" : "bg-slate-50 dark:bg-muted/30"
          }`}>
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="edit-page-visible" className="text-xs font-semibold cursor-pointer">
                  Visible in Navigation
                </Label>
                {page.isHome && (
                  <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                    (Always Public)
                  </span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                {page.isHome
                  ? "The primary Home page is the landing route and cannot be hidden."
                  : "Show or hide this page from public navigation links."}
              </p>
            </div>
            <Switch
              id="edit-page-visible"
              checked={page.isHome ? true : visible}
              disabled={page.isHome}
              onCheckedChange={setVisible}
            />
          </div>

          {/* SEO Accordion */}
          <div className="border border-border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowSeo(!showSeo)}
              className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground bg-muted/20 hover:bg-muted/40 transition-colors"
            >
              <span>SEO & Social Preview Settings</span>
              {showSeo ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>

            {showSeo && (
              <div className="p-3 space-y-3 bg-card border-t border-border">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-page-seo-title" className="text-xs font-medium">
                    SEO Title
                  </Label>
                  <Input
                    id="edit-page-seo-title"
                    value={seoTitle}
                    onChange={(e) => setSeoTitle(e.target.value)}
                    placeholder={title || "Custom page title for search engines"}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-page-seo-desc" className="text-xs font-medium">
                    SEO Meta Description
                  </Label>
                  <Textarea
                    id="edit-page-seo-desc"
                    value={seoDescription}
                    onChange={(e) => setSeoDescription(e.target.value)}
                    placeholder="Snippet shown on Google and social media preview cards..."
                    rows={2}
                    className="text-xs resize-none"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={updatePageMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={updatePageMutation.isPending || (!page.isHome && Boolean(slugError)) || !title.trim()}
            className="gap-1.5"
          >
            {updatePageMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save Settings
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
